import React from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FunnelOverview {
  productName: string;
  totalFunnelSpend: number;
  totalLeads: number;
  customers: number;
  revenue: number;
  funnelCac: number;
  funnelRoas: number;
  profit: number;
  conversionRate: number;
}

export interface FunnelStagePerformance {
  funnel_stage: 'awareness' | 'consideration' | 'conversion';
  spend: number;
  spend_percent: number;
  // Awareness metrics
  reach?: number;
  avg_cpm?: number;
  engagement_rate?: number;
  saves?: number;
  video_views_3s?: number;
  // Consideration metrics
  leads?: number;
  cpl?: number;
  landing_page_views?: number;
  lp_conversion_rate?: number;
  // Conversion metrics
  purchases?: number;
  cac_bottom_only?: number;
  revenue?: number;
}

export function useFunnelPerformance(productName: string | null, dateRange: { from: Date; to: Date }) {
  return useQuery({
    queryKey: ['funnel-performance', productName, dateRange],
    queryFn: async () => {
      if (!productName) return null;

      // Get funnel overview from complete_funnel_roas view
      const { data: overview, error: overviewError } = await supabase
        .from('v_complete_funnel_roas')
        .select('*')
        .eq('product_name', productName)
        .maybeSingle();

      if (overviewError) throw overviewError;
      
      // If no data, return empty structure
      if (!overview) {
        return {
          overview: {
            productName,
            totalFunnelSpend: 0,
            totalLeads: 0,
            customers: 0,
            revenue: 0,
            funnelCac: 0,
            funnelRoas: 0,
            profit: 0,
            conversionRate: 0,
          } as FunnelOverview,
          stages: [],
        };
      }

      // Get stage performance from funnel_performance_by_product view
      const { data: stages, error: stagesError } = await supabase
        .from('v_funnel_performance_by_product')
        .select('*')
        .eq('product_name', productName)
        .order('funnel_stage');

      if (stagesError) throw stagesError;

      // DEPRECATED - leads table no longer used; use deals count instead
      const { count: totalLeads } = await supabase
        .from('deals')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString());

      // Calculate conversion rate
      const conversionRate = overview?.customers && totalLeads 
        ? (overview.customers / totalLeads) * 100 
        : 0;

      return {
        overview: {
          productName,
          totalFunnelSpend: overview?.total_funnel_spend || 0,
          totalLeads: totalLeads || 0,
          customers: overview?.customers || 0,
          revenue: overview?.revenue || 0,
          funnelCac: overview?.funnel_cac || 0,
          funnelRoas: overview?.funnel_roas || 0,
          profit: overview?.profit || 0,
          conversionRate,
        } as FunnelOverview,
        stages: stages as FunnelStagePerformance[] || [],
      };
    },
    enabled: !!productName,
  });
}
